import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Switch, Divider, InputNumber, Row, Col, Button, Card, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Text } = Typography;

const InputNodeProperties = ({ config, form, updateConfig }) => {
  const [inputFields, setInputFields] = useState(config.inputFields || []);
  const prevFieldsRef = React.useRef(inputFields);

  // Sync local state with config when config changes externally (e.g. undo/redo, re-open drawer)
  useEffect(() => {
    if (config.inputFields && JSON.stringify(config.inputFields) !== JSON.stringify(inputFields)) {
      setInputFields(config.inputFields);
      prevFieldsRef.current = config.inputFields;
    }
  }, [config.inputFields]);

  // Update form and persist to config when inputFields change
  useEffect(() => {
    // Only proceed if fields have actually changed
    if (JSON.stringify(prevFieldsRef.current) === JSON.stringify(inputFields)) {
      return;
    }

    form.setFieldsValue({
      inputFields: inputFields
    });

    // Save to config via updateConfig callback
    if (updateConfig) {
      updateConfig({
        config: {
          ...config,
          inputFields: inputFields
        }
      });
    }

    // Update ref
    prevFieldsRef.current = inputFields;
  }, [inputFields, form, updateConfig, config]);

  // Add new input field
  const addInputField = () => {
    const newField = {
      id: `input_${Date.now()}`,
      label: `Input Field ${inputFields.length + 1}`,
      inputType: 'text',
      placeholder: '',
      defaultValue: '',
      required: false,
      variableName: `input_${inputFields.length + 1}`,
      validation: {}
    };
    setInputFields([...inputFields, newField]);
  };

  // Remove input field
  const removeInputField = (index) => {
    const newFields = inputFields.filter((_, i) => i !== index);
    setInputFields(newFields);
  };

  // Update input field
  const updateInputField = (index, updates) => {
    const newFields = [...inputFields];
    newFields[index] = { ...newFields[index], ...updates };
    setInputFields(newFields);
  };

  return (
    <div>
      <Form.Item label="Node Description" name="description" initialValue={config.description || 'Input node for collecting user data'}>
        <Input.TextArea rows={2} placeholder="Describe what this input node collects" />
      </Form.Item>

      <Divider orientation="left">Input Fields Configuration</Divider>
      
      {/* Hidden form field to store inputFields in form */}
      <Form.Item name="inputFields" initialValue={inputFields} style={{ display: 'none' }}>
        <Input />
      </Form.Item>

      {/* Input Fields List */}
      <div style={{ marginBottom: 16 }}>
        {inputFields.map((field, index) => (
          <Card 
            key={field.id}
            size="small" 
            style={{ marginBottom: 12 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>{field.label || `Input Field ${index + 1}`}</Text>
                <Button 
                  type="text" 
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeInputField(index)}
                />
              </div>
            }
          >
            <Row gutter={12}>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Field Label</Text>
                  <Input 
                    size="small"
                    value={field.label}
                    onChange={(e) => updateInputField(index, { label: e.target.value })}
                    placeholder="Field label"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Variable Name</Text>
                  <Input 
                    size="small"
                    value={field.variableName}
                    onChange={(e) => updateInputField(index, { variableName: e.target.value })}
                    placeholder="variable_name"
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Input Type</Text>
                  <Select 
                    size="small"
                    style={{ width: '100%' }}
                    value={field.inputType}
                    onChange={(value) => updateInputField(index, { inputType: value })}
                  >
                    <Option value="text">Text</Option>
                    <Option value="textarea">Text Area</Option>
                    <Option value="number">Number</Option>
                  </Select>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Required</Text>
                  <div>
                    <Switch 
                      size="small"
                      checked={field.required}
                      onChange={(checked) => updateInputField(index, { required: checked })}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Placeholder</Text>
                  <Input 
                    size="small"
                    value={field.placeholder}
                    onChange={(e) => updateInputField(index, { placeholder: e.target.value })}
                    placeholder="Placeholder text"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Default Value</Text>
                  <Input 
                    size="small"
                    value={field.defaultValue}
                    onChange={(e) => updateInputField(index, { defaultValue: e.target.value })}
                    placeholder="Default value"
                  />
                </div>
              </Col>
            </Row>

            {/* Validation Rules */}
            {field.inputType === 'number' && (
              <div>
                <Text style={{ fontSize: '11px', color: '#999' }}>Validation Rules</Text>
                <Row gutter={8}>
                  <Col span={12}>
                    <InputNumber 
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Min value"
                      value={field.validation?.min}
                      onChange={(value) => updateInputField(index, { 
                        validation: { ...field.validation, min: value }
                      })}
                    />
                  </Col>
                  <Col span={12}>
                    <InputNumber 
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Max value"
                      value={field.validation?.max}
                      onChange={(value) => updateInputField(index, { 
                        validation: { ...field.validation, max: value }
                      })}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Add New Field Button */}
      <Button 
        type="dashed" 
        onClick={addInputField}
        block
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
      >
        Add Input Field
      </Button>

      <Divider orientation="left" style={{ fontSize: '12px' }}>Runtime Behavior</Divider>
      
      <div style={{ padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '4px', fontSize: '12px' }}>
        <Text type="secondary">
          <strong>Execution Behavior:</strong><br/>
          • When workflow reaches this node, a popup will appear<br/>
          • User fills in all configured input fields<br/>
          • Data is stored in global variables with specified variable names<br/>
          • Workflow continues to next node
        </Text>
      </div>
    </div>
  );
};

export default InputNodeProperties;